import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import DataTable from "../components/common/DataTable";
import ProfilePhotoInput from "../components/common/ProfilePhotoInput";
import { erpApi, formatDate } from "../services/erpApi";

const specialities = [
  "General Physician",
  "Cardiac",
  "Cardiologist",
  "Dental",
  "Gynaecologist",
  "Dermatologist",
  "Pediatrician",
  "Orthopedic",
  "ENT",
  "Ophthalmologist",
  "Neurologist",
  "Urologist",
  "Diabetologist",
  "Physiotherapist",
  "Pathologist",
  "Radiologist",
  "Other",
];
const emptyDoctor = { name: "", speciality: "General Physician", photo_url: "", phone: "", email: "", working_hours: "", room_no: "" };
const emptyAppointment = { doctor_id: "", patient_id: "", patient_name: "", patient_mobile: "", appointment_date: "", appointment_time: "", reason: "", status: "scheduled" };

export default function Doctors() {
  const [doctors, setDoctors] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctorOpen, setDoctorOpen] = useState(false);
  const [appointmentOpen, setAppointmentOpen] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [doctorForm, setDoctorForm] = useState(emptyDoctor);
  const [appointmentForm, setAppointmentForm] = useState(emptyAppointment);

  const load = async () => {
    const [doctorRes, appointmentRes, patientRes] = await Promise.all([erpApi.doctors(), erpApi.appointments(), erpApi.patients()]);
    setDoctors(doctorRes.data);
    setAppointments(appointmentRes.data);
    setPatients(patientRes.data);
  };

  useEffect(() => {
    load().catch(() => toast.error("Failed to load doctors"));
  }, []);

  const submitDoctor = async (event) => {
    event.preventDefault();
    if (!doctorForm.name || !doctorForm.speciality || !doctorForm.working_hours) {
      toast.error("Doctor name, speciality and working hours are required");
      return;
    }
    if (editingDoctor) {
      await erpApi.updateDoctor(editingDoctor.id, doctorForm);
      toast.success("Doctor updated");
    } else {
      await erpApi.createDoctor(doctorForm);
      toast.success("Doctor added");
    }
    setDoctorOpen(false);
    setEditingDoctor(null);
    setDoctorForm(emptyDoctor);
    load();
  };

  const submitAppointment = async (event) => {
    event.preventDefault();
    if (!appointmentForm.doctor_id || !appointmentForm.patient_name || !appointmentForm.appointment_date || !appointmentForm.appointment_time) {
      toast.error("Doctor, patient name, date and time are required");
      return;
    }
    if (editingAppointment) {
      await erpApi.updateAppointment(editingAppointment.id, appointmentForm);
      toast.success("Appointment updated");
    } else {
      await erpApi.createAppointment(appointmentForm);
      toast.success("Appointment added");
    }
    setAppointmentOpen(false);
    setEditingAppointment(null);
    setAppointmentForm(emptyAppointment);
    load();
  };

  const selectPatient = (patientId) => {
    const patient = patients.find((item) => item.id === patientId);
    setAppointmentForm({
      ...appointmentForm,
      patient_id: patientId,
      patient_name: patient?.name || "",
      patient_mobile: patient?.mobile || "",
    });
  };

  const deleteDoctor = async (doctor) => {
    if (!window.confirm(`Delete ${doctor.name}? Appointments will also be removed.`)) return;
    await erpApi.deleteDoctor(doctor.id);
    toast.success("Doctor deleted");
    load();
  };

  const openAddDoctor = () => {
    setEditingDoctor(null);
    setDoctorForm(emptyDoctor);
    setDoctorOpen(true);
  };

  const openEditDoctor = (doctor) => {
    setEditingDoctor(doctor);
    setDoctorForm({
      name: doctor.name || "",
      speciality: doctor.speciality || "General Physician",
      photo_url: doctor.photo_url || "",
      phone: doctor.phone || "",
      email: doctor.email || "",
      working_hours: doctor.working_hours || "",
      room_no: doctor.room_no || "",
    });
    setDoctorOpen(true);
  };

  const openAddAppointment = () => {
    setEditingAppointment(null);
    setAppointmentForm(emptyAppointment);
    setAppointmentOpen(true);
  };

  const openEditAppointment = (appointment) => {
    setEditingAppointment(appointment);
    setAppointmentForm({
      doctor_id: appointment.doctor_id || "",
      patient_id: appointment.patient_id || "",
      patient_name: appointment.patient_name || "",
      patient_mobile: appointment.patient_mobile || "",
      appointment_date: appointment.appointment_date || "",
      appointment_time: appointment.appointment_time || "",
      reason: appointment.reason || "",
      status: appointment.status || "scheduled",
    });
    setAppointmentOpen(true);
  };

  const deleteAppointment = async (appointment) => {
    if (!window.confirm(`Delete appointment for ${appointment.patient_name}?`)) return;
    await erpApi.deleteAppointment(appointment.id);
    toast.success("Appointment deleted");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row">
        <div>
          <h1 className="font-manrope text-2xl font-bold">Doctors</h1>
          <p className="text-sm text-hospital-text-muted">Doctor speciality, working hours and appointment management.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={appointmentOpen} onOpenChange={(open) => { setAppointmentOpen(open); if (!open) { setEditingAppointment(null); setAppointmentForm(emptyAppointment); } }}>
            <Button variant="outline" onClick={openAddAppointment}><CalendarPlus className="mr-2 h-4 w-4" />Appointment</Button>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{editingAppointment ? "Edit Appointment" : "Add Appointment"}</DialogTitle></DialogHeader>
              <form onSubmit={submitAppointment} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2"><Label>Doctor</Label><Select value={appointmentForm.doctor_id} onValueChange={(value) => setAppointmentForm({ ...appointmentForm, doctor_id: value })}><SelectTrigger><SelectValue placeholder="Select doctor" /></SelectTrigger><SelectContent>{doctors.map((doctor) => <SelectItem key={doctor.id} value={doctor.id}>{doctor.name} - {doctor.speciality}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2"><Label>Existing Patient</Label><Select value={appointmentForm.patient_id || "new"} onValueChange={(value) => value === "new" ? setAppointmentForm({ ...appointmentForm, patient_id: "", patient_name: "", patient_mobile: "" }) : selectPatient(value)}><SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger><SelectContent><SelectItem value="new">New / walk-in</SelectItem>{patients.map((patient) => <SelectItem key={patient.id} value={patient.id}>{patient.name} - {patient.mobile}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2"><Label>Patient Name</Label><Input value={appointmentForm.patient_name} onChange={(e) => setAppointmentForm({ ...appointmentForm, patient_name: e.target.value })} /></div>
                <div className="space-y-2"><Label>Mobile</Label><Input value={appointmentForm.patient_mobile} onChange={(e) => setAppointmentForm({ ...appointmentForm, patient_mobile: e.target.value })} /></div>
                <div className="space-y-2"><Label>Date</Label><Input type="date" value={appointmentForm.appointment_date} onChange={(e) => setAppointmentForm({ ...appointmentForm, appointment_date: e.target.value })} /></div>
                <div className="space-y-2"><Label>Time</Label><Input type="time" value={appointmentForm.appointment_time} onChange={(e) => setAppointmentForm({ ...appointmentForm, appointment_time: e.target.value })} /></div>
                <div className="space-y-2 sm:col-span-2"><Label>Reason</Label><Input value={appointmentForm.reason} onChange={(e) => setAppointmentForm({ ...appointmentForm, reason: e.target.value })} /></div>
                <div className="space-y-2 sm:col-span-2"><Label>Status</Label><Select value={appointmentForm.status} onValueChange={(value) => setAppointmentForm({ ...appointmentForm, status: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="scheduled">Scheduled</SelectItem><SelectItem value="completed">Completed</SelectItem><SelectItem value="cancelled">Cancelled</SelectItem><SelectItem value="no_show">No Show</SelectItem></SelectContent></Select></div>
                <Button className="btn-primary sm:col-span-2">{editingAppointment ? "Update Appointment" : "Save Appointment"}</Button>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={doctorOpen} onOpenChange={(open) => { setDoctorOpen(open); if (!open) { setEditingDoctor(null); setDoctorForm(emptyDoctor); } }}>
            <Button className="btn-primary" onClick={openAddDoctor}><Plus className="mr-2 h-4 w-4" />Add Doctor</Button>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{editingDoctor ? "Edit Doctor" : "Add Doctor"}</DialogTitle></DialogHeader>
              <form onSubmit={submitDoctor} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2"><Label>Doctor Name</Label><Input value={doctorForm.name} onChange={(e) => setDoctorForm({ ...doctorForm, name: e.target.value })} /></div>
                <div className="space-y-2"><Label>Speciality</Label><Select value={doctorForm.speciality} onValueChange={(value) => setDoctorForm({ ...doctorForm, speciality: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{specialities.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent></Select></div>
                <ProfilePhotoInput
                  value={doctorForm.photo_url}
                  onChange={(photoUrl) => setDoctorForm({ ...doctorForm, photo_url: photoUrl })}
                  label="Doctor Photo"
                  className="sm:col-span-2"
                />
                <div className="space-y-2"><Label>Working Hours</Label><Input placeholder="10:00 AM - 2:00 PM" value={doctorForm.working_hours} onChange={(e) => setDoctorForm({ ...doctorForm, working_hours: e.target.value })} /></div>
                <div className="space-y-2"><Label>Phone</Label><Input value={doctorForm.phone} onChange={(e) => setDoctorForm({ ...doctorForm, phone: e.target.value })} /></div>
                <div className="space-y-2"><Label>Email</Label><Input value={doctorForm.email} onChange={(e) => setDoctorForm({ ...doctorForm, email: e.target.value })} /></div>
                <div className="space-y-2"><Label>Room No.</Label><Input value={doctorForm.room_no} onChange={(e) => setDoctorForm({ ...doctorForm, room_no: e.target.value })} /></div>
                <Button className="btn-primary sm:col-span-2">{editingDoctor ? "Update Doctor" : "Save Doctor"}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <DataTable title="Doctor Directory" rows={doctors} empty="No doctors added" columns={[{ key: "name", label: "Doctor", render: (row) => <div className="flex items-center gap-3">{row.photo_url ? <img src={row.photo_url} alt={row.name} className="h-10 w-10 rounded-full object-cover" /> : <div className="h-10 w-10 rounded-full bg-hospital-primary-light" />}<span className="font-semibold text-hospital-text-main">{row.name}</span></div> }, { key: "speciality", label: "Speciality" }, { key: "working_hours", label: "Working Hours" }, { key: "room_no", label: "Room" }, { key: "appointments_count", label: "Appointments" }, { key: "actions", label: "Actions", render: (row) => <div className="flex gap-1"><Button variant="ghost" size="icon" title="Edit doctor" onClick={() => openEditDoctor(row)}><Pencil className="h-4 w-4 text-hospital-primary" /></Button><Button variant="ghost" size="icon" title="Delete doctor" onClick={() => deleteDoctor(row)}><Trash2 className="h-4 w-4 text-red-600" /></Button></div> }]} />
      <DataTable title="Appointments" rows={appointments} empty="No appointments" columns={[{ key: "appointment_date", label: "Date", render: (row) => formatDate(row.appointment_date) }, { key: "appointment_time", label: "Time" }, { key: "doctor_name", label: "Doctor" }, { key: "patient_name", label: "Patient" }, { key: "reason", label: "Reason" }, { key: "status", label: "Status", render: (row) => <span className="capitalize">{row.status?.replace("_", " ")}</span> }, { key: "actions", label: "Actions", render: (row) => <div className="flex gap-1"><Button variant="ghost" size="icon" title="Edit appointment" onClick={() => openEditAppointment(row)}><Pencil className="h-4 w-4 text-hospital-primary" /></Button><Button variant="ghost" size="icon" title="Delete appointment" onClick={() => deleteAppointment(row)}><Trash2 className="h-4 w-4 text-red-600" /></Button></div> }]} />
    </div>
  );
}
